class TrSelect extends HTMLElement {
	selectors = {
		native: '[data-tr-js-select-native]',
		trigger: '[data-tr-js-select-trigger]',
		label: '[data-tr-js-select-label]',
		value: '[data-tr-js-select-value]',
		dropdown: '[data-tr-js-select-dropdown]',
		option: '[data-tr-js-select-option]',
	};

	attributes = {
		block: 'data-tr-js-select-block',
		optionValue: 'data-tr-js-select-option-value',
	};

	stateClasses = {
		isActive: 'is-active',
		isOpen: 'is-open',
	};

	connectedCallback() {
		this.nativeSelectElement = this.querySelector(this.selectors.native);
		this.triggerElement = this.querySelector(this.selectors.trigger);
		this.labelElement = this.querySelector(this.selectors.label);
		this.valueElement = this.querySelector(this.selectors.value);
		this.dropdownElement = this.querySelector(this.selectors.dropdown);
		this.optionElements = [...this.querySelectorAll(this.selectors.option)];
		this.selectedOptionElement = null;

		this.init();
		this.bindEvents();

		document.addEventListener('click', this.onDocumentClick);
		document.addEventListener('shopify:section:load', this.onSectionLoad);
		document.addEventListener('shopify:block:select', this.onBlockSelect);
	}

	disconnectedCallback() {
		document.removeEventListener('click', this.onDocumentClick);
		document.removeEventListener('shopify:section:load', this.onSectionLoad);
		document.removeEventListener('shopify:block:select', this.onBlockSelect);

		this.destroy();
	}

	init() {
		if (!this.nativeSelectElement || !this.triggerElement || !this.dropdownElement) return;

		this.classList.add(this.stateClasses.isActive);
		this.nativeSelectElement.setAttribute('aria-hidden', 'true');
		this.nativeSelectElement.tabIndex = -1;
		this.triggerElement.removeAttribute('aria-hidden');
		this.triggerElement.tabIndex = 0;

		this.updateSelection();
	}

	bindEvents() {
		this.triggerElement?.addEventListener('click', this.onTriggerClick);
		this.triggerElement?.addEventListener('keydown', this.onTriggerKeydown);
		this.nativeSelectElement?.addEventListener('change', this.onNativeChange);

		this.optionElements.forEach(optionElement => {
			optionElement.addEventListener('click', this.onOptionClick);
			optionElement.addEventListener('keydown', this.onOptionKeydown);
		});
	}

	onTriggerClick = () => {
		if (this.triggerElement.disabled) return;

		if (this.classList.contains(this.stateClasses.isOpen)) {
			this.close();
			return;
		}

		this.open();
	};

	onTriggerKeydown = event => {
		if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;

		event.preventDefault();
		this.open(event.key === 'ArrowUp');
	};

	onNativeChange = () => {
		this.updateSelection();
	};

	onOptionClick = event => {
		this.selectOption(event.currentTarget);
	};

	onOptionKeydown = event => {
		const currentIndex = this.optionElements.indexOf(event.currentTarget);

		if (event.key === 'Escape') {
			event.preventDefault();
			this.close(true);
			return;
		}

		if (event.key === 'Tab') {
			this.close();
			return;
		}

		if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

		event.preventDefault();

		if (event.key === 'Home') {
			this.optionElements[0]?.focus();
			return;
		}

		if (event.key === 'End') {
			this.optionElements.at(-1)?.focus();
			return;
		}

		const direction = event.key === 'ArrowDown' ? 1 : -1;
		const nextIndex = (currentIndex + direction + this.optionElements.length) % this.optionElements.length;

		this.optionElements[nextIndex]?.focus();
	};

	onDocumentClick = event => {
		if (this.contains(event.target)) return;

		this.close();
	};

	onSectionLoad = event => {
		if (!this.closest(`#shopify-section-${event.detail.sectionId}`)) return;

		this.init();
	};

	onBlockSelect = event => {
		if (this.getAttribute(this.attributes.block) !== event.detail.blockId) return;

		this.open();
	};

	open(focusLastOption = false) {
		if (!this.dropdownElement || this.triggerElement?.disabled) return;

		this.classList.add(this.stateClasses.isOpen);
		this.triggerElement.setAttribute('aria-expanded', 'true');
		this.dropdownElement.setAttribute('aria-hidden', 'false');

		const optionElement = focusLastOption ? this.optionElements.at(-1) : this.selectedOptionElement;
		optionElement?.focus();
	}

	close(restoreFocus = false) {
		if (!this.dropdownElement || !this.triggerElement) return;

		this.classList.remove(this.stateClasses.isOpen);
		this.triggerElement.setAttribute('aria-expanded', 'false');
		this.dropdownElement.setAttribute('aria-hidden', 'true');

		if (restoreFocus) this.triggerElement.focus();
	}

	selectOption(optionElement) {
		const selectedValue = optionElement.getAttribute(this.attributes.optionValue);

		if (this.nativeSelectElement.value === selectedValue) {
			this.close(true);
			return;
		}

		this.nativeSelectElement.value = selectedValue;
		this.updateSelection();
		this.close(true);

		this.nativeSelectElement.dispatchEvent(new Event('input', { bubbles: true }));
		this.nativeSelectElement.dispatchEvent(new Event('change', { bubbles: true }));
	}

	updateSelection() {
		const selectedValue = this.nativeSelectElement.value;

		this.selectedOptionElement = null;

		this.optionElements.forEach(optionElement => {
			const isSelected = optionElement.getAttribute(this.attributes.optionValue) === selectedValue;

			optionElement.classList.toggle(this.stateClasses.isActive, isSelected);
			optionElement.setAttribute('aria-selected', isSelected ? 'true' : 'false');

			if (isSelected) this.selectedOptionElement = optionElement;
		});

		const selectedLabel = this.selectedOptionElement?.textContent.trim() || '';

		if (this.valueElement) this.valueElement.textContent = selectedLabel;

		const label = this.labelElement?.textContent.trim();
		this.triggerElement.setAttribute('aria-label', label ? `${label} ${selectedLabel}` : selectedLabel);
	}

	destroy() {
		this.triggerElement?.removeEventListener('click', this.onTriggerClick);
		this.triggerElement?.removeEventListener('keydown', this.onTriggerKeydown);
		this.nativeSelectElement?.removeEventListener('change', this.onNativeChange);

		this.optionElements.forEach(optionElement => {
			optionElement.removeEventListener('click', this.onOptionClick);
			optionElement.removeEventListener('keydown', this.onOptionKeydown);
		});

		this.close();
		this.classList.remove(this.stateClasses.isActive);

		if (this.nativeSelectElement) {
			this.nativeSelectElement.removeAttribute('aria-hidden');
			this.nativeSelectElement.removeAttribute('tabindex');
		}

		if (this.triggerElement) {
			this.triggerElement.setAttribute('aria-hidden', 'true');
			this.triggerElement.tabIndex = -1;
		}
	}
}

if (!customElements.get('tr-select')) {
	customElements.define('tr-select', TrSelect);
}
